import React from 'react';

// #region ShareStickyFooterProps
export interface ShareStickyFooterProps {
    completeWalls: number;
    totalWalls: number;
    confirmDisabled: boolean;
    confirming: boolean;
    onConfirm: () => void;
    // Preview mode renders the bar in-flow instead of fixed to the viewport.
    inline?: boolean;
}

// #region ShareStickyFooter
// Bottom action bar: overall progress plus the branded Review action that
// opens the review step (the page only renders this once every wall is done).
// Safe-area padded so it clears the iOS home indicator.
export const ShareStickyFooter: React.FunctionComponent<ShareStickyFooterProps> = (props: ShareStickyFooterProps): JSX.Element => {
    const { completeWalls, totalWalls, confirmDisabled, confirming, onConfirm, inline = false } = props;

    const progress = totalWalls > 0 ? Math.round((completeWalls / totalWalls) * 100) : 0;
    const disabled = confirmDisabled || confirming;

    return (
        <div className={`${inline ? 'sticky' : 'fixed'} inset-x-0 bottom-0 z-30 border-t border-share-border bg-share-raised backdrop-blur`}>
            <div
                className="mx-auto max-w-3xl px-4 pt-3 sm:px-6"
                style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
            >
                <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-share-text">
                            {completeWalls} of {totalWalls} walls complete
                        </div>
                        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-share-primary-soft">
                            <div
                                className="h-full rounded-full transition-all duration-300"
                                style={{ width: `${progress}%`, backgroundColor: 'var(--share-primary)' }}
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={onConfirm}
                        style={disabled
                            ? undefined
                            : { backgroundColor: 'var(--share-primary)', color: 'var(--share-primary-contrast)' }}
                        className={`min-h-[48px] shrink-0 rounded-xl px-5 text-sm font-semibold shadow-sm transition ${
                            disabled ? 'cursor-not-allowed bg-share-surface text-share-muted' : 'hover:brightness-110'
                        }`}
                    >
                        {confirming ? 'Confirming...' : 'Review'}
                    </button>
                </div>
            </div>
        </div>
    );
};
